<template>
    <div class="pimBrandDetail">

        <!-- page-heading -->
        <page-heading :pageTitle="brand ? brand.name : ''" backRoute="primBrands"></page-heading>

        <div class="columns" v-if="!isLoading">

            <div class="col-main">

                <!-- General brand settings -->
                <div class="box">
                    <h2 class="title is-5" style="margin-bottom: 20px;">Allgemeine Einstellungen</h2>
                    
                    <form-input
                        inputType="text"
                        label="Name"
                        :is-horizontal="false"
                        :showInfoText="false"
                        :isReadOnly="true"
                        v-model="brand.name"
                        placeholder="Markenname">
                    </form-input>

                    <div style="margin-top: 15px;">
                        <form-input
                            inputType="text"
                            label="Slug"
                            :is-horizontal="false"
                            :showInfoText="false"
                            :isReadOnly="true"
                            v-model="brand.slug"
                            placeholder="Slug">
                        </form-input>
                    </div>

                    <div style="margin-top: 15px;">
                        <form-input
                            inputType="text"
                            label="URL"
                            :is-horizontal="false"
                            :showInfoText="false"
                            v-model="brand.url"
                            placeholder="https://...">
                        </form-input>
                    </div>

                    <!-- Logo Upload -->
                    <div class="field" style="margin-top: 15px;">
                        <div class="control-label"><label-name label="Logo"></label-name></div>
                        <div class="asset-upload-area" style="max-width: 450px;">
                            <div v-if="logoPreviewUrl && !showLogoDropzone" class="existing-asset">
                                <img :src="logoPreviewUrl" class="asset-preview" />
                                <div class="overlay">
                                    <div class="overlay-actions" style="display: flex; gap: 8px;">
                                        <button class="button is-small" @click.stop="changeLogo" title="Logo ändern">
                                            <i class="fa fa-refresh"></i>
                                        </button>
                                        <button class="button is-small is-danger" @click.stop="deleteLogo" title="Logo löschen">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Dropzone -->
                            <div v-show="!logoPreviewUrl || showLogoDropzone">
                                <dropzone-wrapper
                                    id="brandLogoDropzone"
                                    ref="brandLogoDropzone"
                                    url="fileProcessing/upload?preset=pimBrand&createAssets=1"
                                    :auto-process-queue="true"
                                    :max-files="1"
                                    :max-filesize="16"
                                    dz-class="dzSingle"
                                    dict-default-message="Logo hier ablegen oder klicken"
                                    acceptedFiles="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif,.svg"
                                    @vdropzone-file-added="onLogoFileAdded"
                                    @vdropzone-success="onLogoFileSuccess"
                                    @vdropzone-removed-file="onLogoFileRemoved"
                                    @vdropzone-complete="onLogoUploadComplete">
                                </dropzone-wrapper>
                            </div>
                        </div>
                    </div>

                    <div style="margin-top: 15px;">
                        <form-input 
                            :showInfoText="false" 
                            inputType="switch"
                            v-model="brand.sanova" 
                            inputSize="is-default" 
                            :is-horizontal="false"
                            label="Sanova">
                        </form-input>
                    </div>
                </div>

                <!-- Groups list -->
                <div class="box">
                    <h2 class="title is-5" style="margin-bottom: 10px;">Gruppen</h2>

                    <div class="groups-list">

                        <div
                            v-for="(group, index) in groups"
                            :key="group._key"
                            class="group-item">

                            <div class="group-header">
                                <span class="group-number" v-if="group.name && group.name.trim()">{{ group.name }}</span>
                                <span class="group-number" v-else><lang labelKey="PIM-BRANDS-GROUPS-MODAL-NEW-GROUP"></lang></span>
                                <pop-confirm 
                                    :content="$t('PIM-BRANDS-GROUPS-MODAL-DELETE-GROUP-CONFIRM')" 
                                    icon="question-circle-o" 
                                    :on-ok="function() { removeGroup(index) }" 
                                    :ok-text="$t('PIM-BRANDS-GROUPS-MODAL-BUTTON-DELETE')" 
                                    :cancel-text="$t('PIM-BRANDS-GROUPS-MODAL-BUTTON-CANCEL')" 
                                    :title="$t('PIM-BRANDS-GROUPS-MODAL-DELETE-GROUP-TITLE')">
                                    <button
                                        class="button is-danger is-small is-outlined delete-btn"
                                        :title="$t('PIM-BRANDS-GROUPS-MODAL-DELETE-GROUP')">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </pop-confirm>
                            </div>

                            <div class="group-fields">

                                <!-- Name -->
                                <div class="field">
                                    <form-input
                                        inputType="text"
                                        :label="$t('PIM-BRANDS-GROUPS-MODAL-GROUP-NAME-LABEL')"
                                        :is-horizontal="false"
                                        :showInfoText="false"
                                        v-model="group.name"
                                        :placeholder="$t('PIM-BRANDS-GROUPS-MODAL-GROUP-NAME-PLACEHOLDER')">
                                    </form-input>
                                </div>

                                <!-- Asset Upload -->
                                <div class="field">
                                    <div class="control-label"><label-name labelKey="PIM-BRANDS-GROUPS-MODAL-ASSET-LABEL"></label-name></div>
                                    <div class="asset-upload-area">

                                        <div v-if="group.existingAssetUrl && !group.newFile" class="existing-asset is-cover">
                                            <img :src="group.existingAssetUrl" class="asset-preview" />
                                            <div class="overlay" @click="group.showDropzone = true">
                                                <button class="button is-small">
                                                    <i class="fa fa-refresh"></i>&nbsp;<lang labelKey="PIM-BRANDS-GROUPS-MODAL-CHANGE-ASSET"></lang>
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Dropzone -->
                                        <div v-if="!group.existingAssetUrl || group.showDropzone || group.newFile">
                                            <dropzone-wrapper
                                                :id="'brandGroupDropzone_' + group._key"
                                                url="fileProcessing/upload?preset=pimBrandGroup&createAssets=1"
                                                :auto-process-queue="true"
                                                :max-files="1"
                                                :max-filesize="16"
                                                dz-class="dzSingle"
                                                :dict-default-message="$t('PIM-BRANDS-GROUPS-MODAL-DROPZONE-MESSAGE')"
                                                acceptedFiles="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif,.svg"
                                                @vdropzone-file-added="(file) => onFileAdded(file, index)"
                                                @vdropzone-success="(file, response) => onFileSuccess(response, index)"
                                                @vdropzone-removed-file="() => onFileRemoved(index)"
                                                @vdropzone-complete="(file) => onGroupUploadComplete(group)">
                                            </dropzone-wrapper>
                                        </div>

                                    </div>
                                </div>

                            </div>

                        </div>

                        <!-- Empty state -->
                        <div v-if="groups.length === 0" class="empty-state">
                            <i class="fa fa-folder-open-o"></i>
                            <p>{{ $t('PIM-BRANDS-GROUPS-MODAL-EMPTY-STATE') }}</p>
                        </div>

                    </div>

                    <!-- Add group button -->
                    <div class="add-group-row">
                        <button class="button is-info is-outlined" @click="addGroup">
                            <i class="fa fa-plus"></i>&nbsp;{{ $t('PIM-BRANDS-GROUPS-MODAL-ADD-GROUP') }}
                        </button>
                    </div>
                </div>

            </div>

            <!-- Sidebar sidebox for saving changes -->
            <div class="col-aside">
                <div class="sidebox">
                    <affix :offset="50">
                        <custom-button 
                            :loading="isSaving" 
                            :isDisabled="isUploading"
                            :disabled="isUploading"
                            icon="icon-ak-speichern" 
                            name="Speichern" 
                            type="blue" 
                            @click.native="save">
                        </custom-button>
                    </affix>
                </div>
            </div>

        </div>

        <!-- Loading indicator -->
        <div v-else class="loadingContainer has-text-centered" style="padding: 40px 0;">
            <loading-indicator size="48px"></loading-indicator>
        </div>

    </div>
</template>

<script>
import DropzoneWrapper from '@/components/utils/dropzoneWrapper.vue';

export default {
    name: 'BrandDetail',

    components: {
        DropzoneWrapper
    },

    data() {
        return {
            brand: null,
            groups: [],
            isSaving: false,
            isLoading: false,
            keyCounter: 0,
            newLogoFile: null,
            newLogoPreviewUrl: null,
            uploadedLogoAssetId: null,
            showLogoDropzone: false,
            uploadingStates: {}
        };
    },

    computed: {
        pageTitle() {
            return this.brand && this.brand.name ? this.brand.name : 'Marke bearbeiten';
        },
        existingLogoUrl() {
            if (this.brand && this.brand.logoAsset) {
                return this.getImagePath(this.brand.logoAsset);
            }
            return null;
        },
        logoPreviewUrl() {
            if (this.newLogoPreviewUrl) {
                return this.newLogoPreviewUrl;
            }
            return this.existingLogoUrl;
        },
        isUploading() {
            return Object.keys(this.uploadingStates).length > 0;
        }
    },

    created() {
        const brandId = parseInt(this.$route.params.id);
        if (brandId && Number.isInteger(brandId)) {
            this.fetchBrand(brandId);
        } else {
            this.$notify.open({
                content: 'Ungültige Marken-ID angegeben.',
                type: 'danger'
            });
            this.$router.push({ name: 'primBrands' });
        }
    },

    methods: {

        generateKey() {
            this.keyCounter++;
            return 'grp_' + Date.now() + '_' + this.keyCounter;
        },

        fetchBrand(id, background = false) {
            if (!background) {
                this.isLoading = true;
            }
            this.axios.get(this.$config.api + 'pimBrand/' + id + '?include=pimBrand.logoAsset')
                .then(response => {
                    this.brand = {
                        ...response.data,
                        sanova: response.data.sanova === 1 || response.data.sanova === true
                    };
                    this.loadGroups(background);
                })
                .catch(error => {
                    this.$notify.open({
                        content: 'Fehler beim Laden der Marke: ' + (error.message || 'Unbekannter Fehler'),
                        type: 'danger'
                    });
                    if (!background) {
                        this.isLoading = false;
                    }
                });
        },

        loadGroups(background = false) {
            if (!this.brand || !this.brand.id) return;

            if (!background) {
                this.groups = [];
            }
            this.axios.get(this.$config.api + 'pimBrandGroup?filters[pimBrand]=' + this.brand.id + '&include=pimBrandGroup.asset')
                .then(response => {
                    const items = (response.data.pimBrandGroup && response.data.pimBrandGroup.items)
                        ? response.data.pimBrandGroup.items
                        : (Array.isArray(response.data.pimBrandGroup) ? response.data.pimBrandGroup : []);

                    this.groups = items.map(item => ({
                        _key: this.generateKey(),
                        id: item.id,
                        name: item.name || '',
                        newFile: null,
                        uploadedAssetId: null,
                        existingAssetUrl: item.asset ? this.getImagePath(item.asset) : null,
                        existingAssetId: item.asset ? item.asset.id : null,
                        showDropzone: false
                    }));
                })
                .catch(() => {
                    if (!background) {
                        this.groups = [];
                    }
                })
                .finally(() => {
                    if (!background) {
                        this.isLoading = false;
                    }
                });
        },

        addGroup() {
            this.groups.push({
                _key: this.generateKey(),
                id: null,
                name: '',
                newFile: null,
                uploadedAssetId: null,
                existingAssetUrl: null,
                existingAssetId: null,
                showDropzone: false
            });
        },

        removeGroup(index) {
            const group = this.groups[index];
            this.$delete(this.uploadingStates, group._key);
            if (group.id) {
                this.axios.delete(this.$config.api + 'pimBrandGroup/' + group.id)
                    .then(() => {
                        this.groups.splice(index, 1);
                        this.$notify.open({ 
                            content: this.$t('PIM-BRANDS-GROUPS-MODAL-DELETED-TEXT'), 
                            type: 'warning' 
                        });
                    })
                    .catch(() => {
                        this.$notify.open({ 
                            content: this.$t('PIM-BRANDS-GROUPS-MODAL-DELETED-ERROR-TEXT'), 
                            type: 'danger' 
                        });
                    });
            } else {
                this.groups.splice(index, 1);
            }
        },

        onLogoFileAdded(file) {
            this.$set(this.uploadingStates, 'logo', true);
            if (this.newLogoPreviewUrl) {
                URL.revokeObjectURL(this.newLogoPreviewUrl);
            }
            this.newLogoFile = file;
            this.newLogoPreviewUrl = URL.createObjectURL(file);
            this.uploadedLogoAssetId = null;
            this.showLogoDropzone = false;
        },

        onLogoUploadComplete() {
            this.$delete(this.uploadingStates, 'logo');
        },

        onLogoFileSuccess(file, response) {
            let res = typeof response === 'string' ? JSON.parse(response) : response;
            this.uploadedLogoAssetId = res.id;
        },

        onLogoFileRemoved() {
            this.$delete(this.uploadingStates, 'logo');
            if (this.newLogoPreviewUrl) {
                URL.revokeObjectURL(this.newLogoPreviewUrl);
                this.newLogoPreviewUrl = null;
            }
            this.newLogoFile = null;
            this.uploadedLogoAssetId = null;
            this.showLogoDropzone = false;
        },

        deleteLogo() {
            this.$delete(this.uploadingStates, 'logo');
            if (this.newLogoPreviewUrl) {
                URL.revokeObjectURL(this.newLogoPreviewUrl);
                this.newLogoPreviewUrl = null;
            }
            this.newLogoFile = null;
            this.uploadedLogoAssetId = null;
            if (this.brand) {
                this.brand.logoAsset = null;
            }
            this.showLogoDropzone = false;

            const dz = this.$refs.brandLogoDropzone;
            if (dz && dz.$refs[dz.id]) {
                dz.$refs[dz.id].removeAllFiles(true);
            }
        },

        changeLogo() {
            const el = document.getElementById('brandLogoDropzone');
            if (el) {
                el.click();
            }
        },

        onFileAdded(file, index) {
            const group = this.groups[index];
            this.$set(this.uploadingStates, group._key, true);
            this.$set(group, 'newFile', file);
            this.$set(group, 'uploadedAssetId', null);
        },

        onGroupUploadComplete(group) {
            this.$delete(this.uploadingStates, group._key);
        },

        onFileSuccess(response, index) {
            let res = typeof response === 'string' ? JSON.parse(response) : response;
            this.$set(this.groups[index], 'uploadedAssetId', res.id);
        },

        onFileRemoved(index) {
            const group = this.groups[index];
            this.$delete(this.uploadingStates, group._key);
            this.$set(group, 'newFile', null);
            this.$set(group, 'uploadedAssetId', null);
        },

        async save() {
            if (this.isUploading) {
                return;
            }
            // Validation: Ensure every group has a name and an asset
            const isInvalid = this.groups.some(group => {
                const hasName = !!(group.name && group.name.trim().length > 0);
                const hasAsset = !!(group.uploadedAssetId || group.existingAssetId);
                const isUploading = !!(group.newFile && !group.uploadedAssetId);
                return !hasName || !hasAsset || isUploading;
            });

            if (isInvalid) {
                const isUploading = this.groups.some(group => !!(group.newFile && !group.uploadedAssetId));
                if (isUploading) {
                    this.$notify.open({ 
                        content: this.$t('PIM-BRANDS-GROUPS-MODAL-WARNING-TEXT'), 
                        type: 'warning' 
                    });
                    return;
                }

                this.$notify.open({ 
                    content: this.$t('PIM-BRANDS-GROUPS-MODAL-ERROR-TEXT'),
                    type: 'danger' 
                });
                return;
            }

            this.isSaving = true;

            try {
                // 1. Save brand name, URL, logoAsset and sanova toggle
                let logoAssetId = this.uploadedLogoAssetId || (this.brand.logoAsset ? this.brand.logoAsset.id : null);
                await this.axios.post(this.$config.api + 'pimBrand', {
                    id: this.brand.id,
                    name: this.brand.name,
                    url: this.brand.url,
                    logoAsset: logoAssetId,
                    sanova: this.brand.sanova === true ? 1 : 0
                });

                // 2. Save brand groups
                const promises = this.groups.map(async (group) => {
                    let assetId = group.uploadedAssetId || group.existingAssetId || null;

                    const payload = {
                        name: group.name,
                        pimBrand: this.brand.id,
                        asset: assetId
                    };

                    if (group.id) {
                        return this.axios.put(this.$config.api + 'pimBrandGroup/' + group.id, { id: group.id, ...payload });
                    } else {
                        return this.axios.post(this.$config.api + 'pimBrandGroup', payload);
                    }
                });

                await Promise.all(promises);

                this.$notify.open({ content: 'Marke und Gruppen erfolgreich gespeichert!', type: 'success' });
                
                this.newLogoFile = null;
                this.newLogoPreviewUrl = null;
                this.uploadedLogoAssetId = null;
                this.showLogoDropzone = false;
                
                this.fetchBrand(this.brand.id, true);

            } catch (e) {
                this.$notify.open({ content: 'Fehler beim Speichern: ' + (e.message || 'Unbekannter Fehler'), type: 'danger' });
            } finally {
                this.isSaving = false;
            }
        },

        getImagePath(asset, thumb=false) {
            if(!asset) {
                return "";
            }
            let path = asset.path ? asset.path : 'dam/pimBrandGroup';
            return this.$config.cdnHost + "apokat-backend/assets/" + path + "/" + (thumb != false ? '150/' : '') + asset.filename;
        }

    }
};
</script>

<style lang="scss" scoped>
.groups-list {
    display: flex;
    flex-direction: column;
    gap: 16px 0;
    max-height: 52vh;
    overflow-y: auto;
    padding-right: 4px;
}

.group-item {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;
    background: #fafafa;
    transition: border-color .2s;

    &:hover {
        border-color: #c8c8c8;
    }
}

.group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .group-number {
        font-weight: 600;
        font-size: 13px;
        color: #555;
        text-transform: uppercase;
        letter-spacing: .5px;
    }

    .delete-btn {
        border-color: transparent;
        color: #ccc;
        background: transparent;
        transition: color .2s, border-color .2s;

        &:hover {
            color: #e74c3c;
            border-color: #e74c3c;
            background: transparent;
        }
    }
}

.group-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 16px;
}

.existing-asset {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 1.2px dashed #dedede;
    border-radius: 4px;
    background: #fff;
    min-height: 126px;
    overflow: hidden;

    .asset-preview {
        max-width: 100%;
        max-height: 106px;
        object-fit: contain;
    }

    &.is-cover {
        padding: 0;

        .asset-preview {
            width: 100%;
            height: 126px;
            max-width: 100%;
            max-height: 126px;
            object-fit: cover;
        }
    }

    .overlay {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(255, 255, 255, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        cursor: pointer;

        .button {
            font-size: 12px;
            pointer-events: none;
        }

        .overlay-actions .button {
            pointer-events: auto;
        }
    }

    &:hover .overlay {
        opacity: 1;
    }
}

.asset-upload-area {
    ::v-deep .customdropzone {
        min-height: 70px;
        font-size: 11px;
    }
    ::v-deep #brandLogoDropzone.customdropzone {
        min-height: 125px !important;
        height: 125px !important;
    }
    ::v-deep .dz-message {
        font-size: 11px;
    }
}

.empty-state {
    text-align: center;
    padding: 32px 16px;
    color: #aaa;

    i {
        font-size: 32px;
        display: block;
        margin-bottom: 8px;
    }

    p {
        font-size: 14px;
    }
}

.add-group-row {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid #eee;

    .button {
        font-size: 13px;
    }
}
</style>
